import { useEffect, useState } from 'react';
import type { Strings } from '../lib/i18n';
import { fmt } from '../lib/i18n';
import { NOT_LIVE_MAX_TRIES, session, type SessionState } from '../lib/session';

function useCountdown(retryAt: number | null): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!retryAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [retryAt]);
  return retryAt ? Math.max(0, Math.ceil((retryAt - now) / 1000)) : 0;
}

/** Banner explaining why chat is not flowing, with the retry countdown. */
export function StatusBanner({ s, t }: { s: SessionState; t: Strings }) {
  const secs = useCountdown(s.retryAt);
  if (s.status === 'live' || s.status === 'idle') return null;
  if (s.status === 'connecting' && !s.message) return null;
  let message = s.message;
  if (s.retryAt) {
    if (s.status === 'slots_full') message = fmt(t.statusFull, { max: s.slots?.max ?? '', s: secs });
    else if (s.status === 'busy') message = fmt(t.statusBusy, { s: secs });
    else if (s.status === 'connecting') message = fmt(t.statusReconnecting, { s: secs });
    else if (s.status === 'offline' || s.status === 'restricted') message = `${s.message} ${fmt(t.retryingIn, { s: secs, n: s.notLiveTries, max: NOT_LIVE_MAX_TRIES })}`;
  } else if ((s.status === 'offline' || s.status === 'restricted') && s.notLiveTries >= NOT_LIVE_MAX_TRIES) {
    message = `${s.message} ${fmt(t.retryGaveUp, { max: NOT_LIVE_MAX_TRIES })}`;
  }
  const warn = s.status === 'error' || s.status === 'kicked' || s.status === 'slots_full' || s.status === 'offline' || s.status === 'restricted';
  const showRetry = s.status === 'offline' || s.status === 'restricted' || s.status === 'ended' || s.status === 'error' || Boolean(s.retryAt);
  return (
    <div className={`banner ${warn ? 'banner--warn' : 'banner--info'}`} role="status">
      <span>{message}</span>
      {showRetry && (
        <button type="button" className="btn" onClick={() => session.retryNow()}>
          {s.status === 'offline' || s.status === 'restricted' || s.status === 'ended' || s.status === 'error' ? t.tryAgain : t.retryNow}
        </button>
      )}
    </div>
  );
}
